import { Card, CardContent, CardHeader } from '@mui/material';
import {
    FormInputGroup,
    FormText,
    FormSelect,
    FormAddress,
} from 'components/form';
import { SupplierDeliveryType, SupplierGroup } from 'types';
import { FormSelectOption } from 'components/form/types';
import styles from '../../DeviceConfig.module.scss';
import { useSupplierGroups } from 'hooks/useSupplierGroups';
import { useEffect, useMemo, useState } from 'react';
import { useSupplierDeliveryTypes } from 'hooks/useSupplierDeliveryTypes';
import { DeviceConfigInput } from '../../DeviceConfig';

/** Info section of the device details > config page. */
export const DeviceConfigInfo = ({
    defaultValues,
}: {
    defaultValues?: DeviceConfigInput;
}) => {
    const groups = useSupplierGroups();
    const defaultGroup = defaultValues?.group;
    const defaultGroupOpts = useMemo(() => {
        return defaultGroup
            ? [{ value: defaultGroup }]
            : [{ name: 'None', value: '' }];
    }, [defaultGroup]);
    const [groupOptions, setGroupOptions] =
        useState<FormSelectOption[]>(defaultGroupOpts);
    const deliveryTypes = useSupplierDeliveryTypes();
    const [deliveryTypeOptions, setDeliveryTypeOptions] =
        useState<FormSelectOption[]>();

    // Construct group dropdown options.
    useEffect(() => {
        if (groups) {
            setGroupOptions(getGroupOptions(groups));
        } else {
            setGroupOptions(defaultGroupOpts);
        }
    }, [groups, defaultGroupOpts]);

    // Construct delivery type dropdown options.
    useEffect(() => {
        if (deliveryTypes) {
            setDeliveryTypeOptions(getDeliveryTypeOptions(deliveryTypes));
        } else {
            setDeliveryTypeOptions([{ name: 'None', value: '' }]);
        }
    }, [deliveryTypes]);

    const getGroupOptions = (groups: SupplierGroup[]): FormSelectOption[] => {
        const defaultOption: FormSelectOption = { name: 'None', value: '' };
        const groupOptions = groups?.map((group: SupplierGroup) => {
            return { value: group.name };
        });
        if (groupOptions) return [defaultOption, ...groupOptions];
        else return [defaultOption];
    };

    const getDeliveryTypeOptions = (
        deliveryTypes: SupplierDeliveryType[]
    ): FormSelectOption[] => {
        const defaultOption: FormSelectOption = { name: 'None', value: '' };
        const deliveryTypeOptions = deliveryTypes.map(
            (deliveryType: SupplierDeliveryType) => {
                const formSelectOpt: FormSelectOption = {
                    name: deliveryType.deliveryTypeName,
                    value: deliveryType.deliveryTypeName,
                };
                return formSelectOpt;
            }
        );
        if (deliveryTypeOptions) return [defaultOption, ...deliveryTypeOptions];
        else return [defaultOption];
    };

    return (
        <div data-testid="DeviceConfigInfo" className={styles.section}>
            <Card className={styles.card}>
                <CardHeader title="Info" />
                <CardContent>
                    <FormInputGroup>
                        <FormText field="customer_name" label="Customer Name" />
                        <FormText field="name" label="Tank Name" />
                    </FormInputGroup>
                    <FormAddress field="address" />
                    <FormInputGroup>
                        <FormSelect
                            field="delivery_type"
                            options={
                                deliveryTypeOptions ?? [
                                    { name: 'None', value: '' },
                                ]
                            }
                        />

                        <FormSelect field="group" options={groupOptions} />
                    </FormInputGroup>
                    <FormInputGroup>
                        <FormText
                            field="billing.pricePerGallon"
                            label="Price Per Gallon"
                            isNumber={true}
                        />
                        <FormText
                            field="billing.taxPercentage"
                            label="Tax Percentage"
                            isNumber={true}
                        />
                    </FormInputGroup>
                </CardContent>
            </Card>
        </div>
    );
};
